import React, { useContext, RefObject } from 'react';
import { withScriptjs, withGoogleMap, GoogleMap, Marker, InfoWindow } from 'react-google-maps';
import { TCollection } from '@com/core/common/types/customer_address';
import { getMarketplaceConfig } from '@com/core/common/utils/config';
import { MARKETPLACE } from '@com/core/common/baseConfig';
import { MapContext } from '@com/core/common/contexts/mapContext';
import InfoWindowContent from '@com/core/common/components/map/InfoWindowContent';
import { createGlobalStyle } from 'styled-components';
import { getMapStyle } from '@com/core/common/styles/MapStyle';
import { TMarker } from '@com/core/common/types/map/googlemap';
import { AppContext } from '@com/core/common/contexts/appContext';

type TProps = {
    collectionPointsEnabled: boolean;
    customMarkers?: TMarker[];
    customCenter?: { lat: number; lng: number };
    mapRef?: RefObject<GoogleMap>;
};

const BaseMap = ({ collectionPointsEnabled, customCenter, customMarkers, mapRef }: TProps): JSX.Element => {
    const {
        clickedLocation,
        collectionPoints,
        handleCollectionPointClick,
        handleMapClickDaily,
        mapProps,
        selectedCollectionPoint,
    } = useContext(MapContext);

    const { locale } = useContext(AppContext);

    const config = getMarketplaceConfig(MARKETPLACE);

    const region = locale.apiRegion;

    const mapStyle = getMapStyle();

    const GlobalMapStyle = createGlobalStyle`
        div.gm-style-iw {
            width: 150px;
        }
        button.gm-ui-hover-effect {
            top: 0px !important;
            right: 0px !important;
        }

        html[dir=rtl] button.gm-ui-hover-effect{
            top: 0px !important;
            right: 120px !important;
        }
    `;

    let defaultCenter = {
        lat: config.map.centralGeoLocations[region].lat / config.map.precision,
        lng: config.map.centralGeoLocations[region].lng / config.map.precision,
    };

    if (clickedLocation) {
        defaultCenter = {
            lat: clickedLocation.lat / config.map.precision,
            lng: clickedLocation.lng / config.map.precision,
        };
    }

    if (customCenter) {
        defaultCenter = {
            lat: customCenter.lat,
            lng: customCenter.lng,
        };
    }

    return (
        <>
            <GlobalMapStyle />
            <GoogleMap
                options={{
                    gestureHandling: 'cooperative',
                    disableDefaultUI: true,
                    mapTypeId: google.maps.MapTypeId.ROADMAP,
                    draggable: true,
                    styles: mapStyle || undefined,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    scaleControl: false,
                    zoomControl: true,
                    disableDoubleClickZoom: true,
                    clickableIcons: false,
                    ...mapProps,
                }}
                defaultZoom={config.map.defaultZoom}
                defaultCenter={defaultCenter}
                ref={mapRef}
                onClick={e => handleMapClickDaily({ lat: e.latLng.lat(), lng: e.latLng.lng() })}>
                {clickedLocation && (
                    <Marker
                        key={`marker-${clickedLocation.lat}-${clickedLocation.lat}`}
                        position={{
                            lat: clickedLocation.lat / config.map.precision,
                            lng: clickedLocation.lng / config.map.precision,
                        }}
                        icon={{
                            url: config.assets.images.mapLandmarkPin,
                        }}
                        draggable={false}>
                        {clickedLocation && clickedLocation.city && clickedLocation.area && (
                            <InfoWindow
                                onCloseClick={(): void => {
                                    handleMapClickDaily(null);
                                }}>
                                <InfoWindowContent
                                    mainText={clickedLocation.city.name}
                                    secondaryText={clickedLocation.area}
                                />
                            </InfoWindow>
                        )}
                    </Marker>
                )}

                {collectionPointsEnabled &&
                    Array.isArray(collectionPoints) &&
                    collectionPoints.map((collectionPoint: TCollection) => {
                        return (
                            <Marker
                                key={collectionPoint.collectionPointCode}
                                clickable
                                draggable={false}
                                position={{
                                    lat: collectionPoint.lat / config.map.precision,
                                    lng: collectionPoint.lng / config.map.precision,
                                }}
                                icon={{
                                    url:
                                        collectionPoint.collectionType &&
                                        collectionPoint.collectionType === 'pickup_point'
                                            ? config.assets.images.mapCollectionPointPickup
                                            : config.assets.images.mapCollectionPointLocker,
                                    scaledSize: { width: 42, height: 47, equals: (): boolean => false },
                                }}
                                onClick={(): void => {
                                    handleCollectionPointClick(collectionPoint.collectionPointCode);
                                }}>
                                {selectedCollectionPoint &&
                                    collectionPoint.collectionPointCode &&
                                    collectionPoint.collectionPointCode ===
                                        selectedCollectionPoint.collectionPointCode && (
                                        <InfoWindow
                                            onCloseClick={(): void => {
                                                handleCollectionPointClick(null);
                                            }}>
                                            <InfoWindowContent
                                                mainText={collectionPoint.city.name}
                                                secondaryText={collectionPoint.area}
                                            />
                                        </InfoWindow>
                                    )}
                            </Marker>
                        );
                    })}

                {!!customMarkers &&
                    customMarkers.map(customMarker => (
                        <Marker icon={customMarker.icon} key={customMarker.key} position={customMarker.position} />
                    ))}
            </GoogleMap>
        </>
    );
};

export default withScriptjs(withGoogleMap(BaseMap));
